import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { 
    Box, 
    Button, 
    TextField, 
    Typography, 
    Paper, 
    Container, 
    CircularProgress, 
    Alert,
    styled,
    alpha,
    Fade,
    Divider,
    Stack
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { cyber } from '../theme';

interface AccessCodePageProps {
  onSuccess: () => void;
}

const LoginContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${cyber.bgDefault} 0%, ${cyber.bgSidebar} 50%, ${cyber.bgPaper} 100%)`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: `radial-gradient(circle, ${alpha(cyber.primary, 0.1)} 0%, transparent 70%)`,
        animation: 'pulse 15s ease-in-out infinite',
    },
    '@keyframes pulse': {
        '0%, 100%': { transform: 'scale(1)' },
        '50%': { transform: 'scale(1.1)' },
    }
}));

const AccessCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '500px',
    position: 'relative',
    zIndex: 1,
    background: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(20px)',
    borderRadius: theme.spacing(2),
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const SubmitButton = styled(Button)(({ theme }) => ({
    background: cyber.gradient,
    color: 'white',
    fontWeight: 'bold',
    padding: '12px 24px',
    fontSize: '1rem',
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    boxShadow: `0 4px 16px ${alpha(cyber.primary, 0.4)}`,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        background: cyber.gradientHover,
        boxShadow: cyber.glow,
        transform: 'translateY(-2px)',
    },
    '&:disabled': {
        background: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
    }
}));

const RequestButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    color: theme.palette.text.secondary,
    '&:hover': {
        background: alpha(theme.palette.primary.main, 0.05),
        color: theme.palette.primary.main,
    }
}));

const AccessCodePage: React.FC<AccessCodePageProps> = ({ onSuccess }) => {
    const { accounts } = useMsal(); // Mantenuto per 'handleSubmit'
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Per il form 'Verify Code'
    const [isRequesting, setIsRequesting] = useState(false); // Per il form 'Request Code'
    const [error, setError] = useState<string | null>(null);
    const [requestSuccess, setRequestSuccess] = useState(false);
    
    // --- NUOVI STATI ---
    const [showEmailInput, setShowEmailInput] = useState(false);
    const [requestedEmail, setRequestedEmail] = useState('');
    // --- FINE NUOVI STATI ---

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);
        setError(null);
        setRequestSuccess(false); // Pulisce il messaggio di successo della richiesta

        console.log("1. Funzione handleSubmit avviata.");

        const logicAppUrl = process.env.REACT_APP_LOGIC_APP_URL;
        console.log("2. URL della Logic App letto:", logicAppUrl);

        if (!logicAppUrl) {
            console.error("ERRORE: La variabile d'ambiente REACT_APP_LOGIC_APP_URL non è stata trovata!");
            setError("Configuration error: verification service URL not found.");
            setIsLoading(false);
            return;
        }

        const userId = accounts[0]?.idTokenClaims?.oid;
        console.log("3. ID Utente (oid) recuperato:", userId);

        if (!userId) {
            console.error("ERRORE: Impossibile recuperare l'ID utente dal token MSAL.");
            setError("Unable to identify user. Please try logging in again.");
            setIsLoading(false);
            return;
        }

        try {
            console.log("4. Sto per eseguire la chiamata fetch a:", logicAppUrl);
            
            const response = await fetch(logicAppUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessCode: code, userId: userId })
            });

            console.log("5. Chiamata fetch completata. Status risposta:", response.status);

            if (!response.ok) {
                const errorData = await response.json();
                console.error("ERRORE dalla Logic App:", errorData);
                throw new Error(errorData.message || 'Invalid or already used code.');
            }
            
            console.log("6. Codice verificato con successo!");
            sessionStorage.setItem('isCodeVerified', 'true');
            onSuccess();

        } catch (e: any) {
            console.error("7. Errore catturato nel blocco catch:", e);
            setError(e.message);
        } finally {
            console.log("8. Blocco finally eseguito.");
            setIsLoading(false);
        }
    };

    // --- NUOVA FUNZIONE PER MOSTRARE IL FORM ---
    const handleShowRequestForm = () => {
        setShowEmailInput(true);
        setRequestSuccess(false); // Nasconde il successo precedente
        setError(null); // Pulisce errori precedenti
    };

    // --- FUNZIONE MODIFICATA PER INVIARE LA RICHIESTA EMAIL ---
    const handleSubmitEmailRequest = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsRequesting(true);
        setError(null);
        setRequestSuccess(false);

        console.log("Requesting code for email:", requestedEmail);

        // Email validation - basic format check
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!requestedEmail || !emailRegex.test(requestedEmail)) {
            setError("Please enter a valid email address.");
            setIsRequesting(false);
            return;
        }

        // Security fix: Remove hardcoded URL, require environment variable
        const requestCodeUrl = process.env.REACT_APP_REQUEST_CODE_URL;

        if (!requestCodeUrl) {
            console.error("ERRORE: La variabile d'ambiente REACT_APP_REQUEST_CODE_URL non è stata trovata!");
            setError("Configuration error: request code service URL not found.");
            setIsRequesting(false);
            return;
        }

        try {
            const response = await fetch(requestCodeUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // --- MODIFICA: Usa l'email dallo stato 'requestedEmail' ---
                body: JSON.stringify({ email: requestedEmail })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to request access code.');
            }

            setRequestSuccess(true);
            console.log("Access code request sent successfully!");
            setShowEmailInput(false); // Nasconde il form dopo il successo
            setRequestedEmail(''); // Pulisce il campo

        } catch (e: any) {
            console.error("Error requesting access code:", e);
            setError(e.message);
        } finally {
            setIsRequesting(false);
        }
    };

    return (
        <LoginContainer>
            <Container maxWidth="sm">
                <Fade in timeout={600}>
                    <AccessCard elevation={8}>
                        {/* Lock Icon */}
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: cyber.gradient,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 3,
                                boxShadow: cyber.glow,
                            }}
                        >
                            <LockIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>

                        {/* Title */}
                        <Typography 
                            component="h1" 
                            variant="h4" 
                            fontWeight="bold"
                            sx={{
                                mb: 1,
                                ...cyber.gradientText,
                            }}
                        >
                            Early Access
                        </Typography>

                        <Typography color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                            This is a beta version. Please enter your access code to continue.
                        </Typography>

                        {/* Form Principale (Verifica Codice) */}
                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="accessCode"
                                label="Access Code"
                                name="accessCode"
                                autoFocus
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                            
                            <SubmitButton
                                type="submit"
                                fullWidth
                                sx={{ mt: 3, mb: 2 }}
                                disabled={isLoading || !code}
                            >
                                {isLoading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Verify Code'
                                )}
                            </SubmitButton>
                        </Box>

                        {/* Alert per errori (mostra errori da entrambi i form) */}
                        {error && (
                            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                                {error}
                            </Alert>
                        )}

                        {/* Divider */}
                        <Divider sx={{ width: '100%', my: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                or
                            </Typography>
                        </Divider>

                        {/* --- BLOCCO UI MODIFICATO PER RICHIESTA CODICE --- */}
                        {!showEmailInput ? (
                            // 1. Stato iniziale: mostra solo il pulsante
                            <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Don't have an access code?
                                </Typography>
                                <RequestButton
                                    variant="text"
                                    startIcon={<EmailIcon />}
                                    onClick={handleShowRequestForm} // Chiama la nuova funzione
                                >
                                    Request one now
                                </RequestButton>
                            </Stack>
                        ) : (
                            // 2. Stato secondario: mostra il form per l'email
                            <Box component="form" onSubmit={handleSubmitEmailRequest} noValidate sx={{ width: '100%' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                                    Please enter your email to receive an access code.
                                </Typography>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="requestEmail"
                                    label="Your Email Address"
                                    name="requestEmail"
                                    autoComplete="email"
                                    autoFocus
                                    value={requestedEmail}
                                    onChange={(e) => setRequestedEmail(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                                    }}
                                />
                                <SubmitButton
                                    type="submit"
                                    fullWidth
                                    sx={{ mt: 2, mb: 2 }}
                                    disabled={isRequesting || !requestedEmail}
                                >
                                    {isRequesting ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        'Submit Request'
                                    )}
                                </SubmitButton>
                            </Box>
                        )}

                        {/* Alert per successo richiesta (mostrato solo dopo l'invio) */}
                        {requestSuccess && (
                            <Alert 
                                severity="success" 
                                icon={<CheckCircleIcon />}
                                sx={{ width: '100%', mt: 2 }}
                            >
                                Access code request sent! Check your email inbox.
                            </Alert>
                        )}
                        {/* --- FINE BLOCCO UI MODIFICATO --- */}


                        {/* Footer Note */}
                        <Box 
                            sx={{ 
                                mt: 4, 
                                pt: 3, 
                                borderTop: 1, 
                                borderColor: 'divider',
                                width: '100%',
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Access codes will be sent to the email address you provide.
                            </Typography>
                        </Box>
                    </AccessCard>
                </Fade>
            </Container>
        </LoginContainer>
    );
};

export default AccessCodePage;